import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import React from 'react';
import { Card, CardBody, CardTitle, Col, ColProps, Input, Row } from 'reactstrap';

import elements from 'core/config/elements';
import { LocalizedMessage } from 'core/l10n/components';
import { RewardCalculation } from 'core/ovations-api/definitions';
import { RewardCalculationType, RewardPackageType } from 'core/ovations-api/enums';
import FAIcon from 'core/ovations-components/FAIcon';
import { monetize } from 'core/util/strings';

export interface AvailableRewardsProps {
    rewardCalculation: RewardCalculation;
    onChange: (rewardCalculationId: number) => void;
    wasValidated?: boolean;
    selectedReward: number | null;
    isRequired: boolean;
    choiceRewardNumber: number;
    columnWidths?: ColProps;
    renderErrorBanner?: (banner: JSX.Element) => JSX.Element;
    mustAcceptEsign?: boolean;
    isReadOnly?: boolean;
    showErrorBanner?: boolean;
    shouldRenderCustomerNote?: boolean;
}

const AvailableRewards: React.SFC<AvailableRewardsProps> = (props) => {
    const { rewardCalculation } = props;
    const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const { value } = e.currentTarget;
        if (props.onChange) {
            props.onChange(Number(value));
        }
    };

    const renderErrorBanner = (): JSX.Element | null => {
        if (props.selectedReward || !props.isRequired || !props.wasValidated) {
            return null;
        }
        const banner = (
            <div id={elements.availableRewards.id.rewardErrorBanner} className="d-flex align-items-center">
                <FAIcon icon={faExclamationTriangle} size="2x" className="me-3 mb-3 text-danger" />
                <div className="alert alert-danger text-danger fw-bold px-3 py-2 border-0 rounded-0" role="alert">
                    <LocalizedMessage id="claimDetail_errorMessage_rewardNotSelected" />
                </div>
            </div>
        );
        return props.renderErrorBanner ? props.renderErrorBanner(banner) : banner;
    };

    const generateChoiceRewardValue = (rewardCalculation: RewardCalculation) => {
        if (rewardCalculation.type === RewardCalculationType.Static && rewardCalculation.amount) {
            if (rewardCalculation.rewardPackages.some((x) => x.rewardPackageType === RewardPackageType.Points)) {
                return (
                    <>
                        {Math.round(rewardCalculation.amount).toString()} <LocalizedMessage id="label_points" />
                    </>
                );
            }
            return monetize(rewardCalculation.amount);
        }
        return '';
    };

    const renderAvailableRewards = (rewards: RewardCalculation[]): JSX.Element[] | undefined => {
        if (!rewards) {
            return;
        }
        const columnWidths = props.columnWidths ? props.columnWidths : { xs: 12, lg: 4 };
        const promotionRewards: JSX.Element[] = rewards
            .sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))
            .map((rewardCalc) => {
                const showActionRequiredMessage =
                    rewardCalc.isEsignEnabled &&
                    props.mustAcceptEsign &&
                    rewardCalc.rewardPackages.some((x) => x.rewardPackageType === RewardPackageType.VirtualUSA);
                return (
                    <Col className="vertical-grid-gutter text-center" {...columnWidths} key={rewardCalc.id}>
                        <Card
                            tag="label"
                            className={classNames(
                                'm-0 h-100 card--selectable',
                                elements.availableRewards.class.option,
                                {
                                    active: props.selectedReward === rewardCalc.id,
                                },
                                { 'card--not--selectable': showActionRequiredMessage },
                            )}
                        >
                            <CardBody>
                                <CardTitle>
                                    <div className="h6 fw-bold">{generateChoiceRewardValue(rewardCalc)}</div>
                                    <div className="h6 text-muted fw-normal pb-1">{rewardCalc.label}</div>
                                </CardTitle>
                                <span className="label text-nowrap form-check">
                                    <Input
                                        type="radio"
                                        name={`promotionRewards_${rewardCalculation.id}`}
                                        onChange={onChange}
                                        checked={props.selectedReward === rewardCalc.id}
                                        required={props.isRequired}
                                        disabled={showActionRequiredMessage || props.isReadOnly}
                                        value={rewardCalc.id}
                                    />
                                    <LocalizedMessage id="claimDetail_label_selectReward" />
                                </span>
                                {showActionRequiredMessage && (
                                    <div className="text-danger text-smaller">
                                        <span className="fw-bold">Customer Action Required</span>
                                        <span className="font-italic">
                                            : Customer must accept esign agreement to select eCard Reward
                                        </span>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                );
            });
        return promotionRewards;
    };

    if (rewardCalculation && rewardCalculation.type === RewardCalculationType.Choice) {
        return (
            <div className="mt-3 d-block">
                {props.showErrorBanner && renderErrorBanner()}
                <h3 className="h5 d-block">
                    <LocalizedMessage id="claimDetail_heading_availableRewards" />
                    {props.choiceRewardNumber}
                </h3>
                <div className="d-block mb-4">
                    <LocalizedMessage id="claimDetail_heading_selectReward" />
                    {props.shouldRenderCustomerNote && (
                        <span className="ps-3 fw-bold" id={elements.availableRewards.id.rewardNote}>
                            <LocalizedMessage id="claimDetail_heading_choice_note" />
                        </span>
                    )}
                </div>
                <Row className={classNames({ 'card--disabled': props.isReadOnly })}>
                    {renderAvailableRewards(rewardCalculation.choices)}
                </Row>
            </div>
        );
    }
    return null;
};

export default AvailableRewards;
